import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { base_url, config } from '../utils/axiosConfig';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const AUTO_SLIDE_INTERVAL = 3000;

const RecentlyAddProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [productsPerPage, setProductsPerPage] = useState(4);
  const navigate = useNavigate();

  const ORANGE = '#ff6701';
  const DARK = '#232f3e';

  useEffect(() => {
    const updateProductsPerPage = () => {
      if (window.innerWidth <= 576) setProductsPerPage(1);
      else if (window.innerWidth <= 768) setProductsPerPage(2);
      else if (window.innerWidth <= 992) setProductsPerPage(3);
      else setProductsPerPage(4);
    };

    updateProductsPerPage();
    window.addEventListener('resize', updateProductsPerPage);
    return () => window.removeEventListener('resize', updateProductsPerPage);
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${base_url}Products`, config);
        const data = Array.isArray(response.data)
          ? response.data
          : response.data?.products || [];
          
        const validProducts = data.filter(p => {
          const selectedVariant = p.variants?.[0] || null;
          const currentPrice = selectedVariant ? selectedVariant.CurrentPrice : p.CurrentPrice;
          return currentPrice && currentPrice > 0;
        });
        
        setProducts(validProducts.slice(0, 12));
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const maxIndex = products.length > 0 ? Math.ceil(products.length / productsPerPage) - 1 : 0;

  const handlePrev = () => {
    setCurrentIndex(prev => (prev === 0 ? maxIndex : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
  };

  useEffect(() => {
    if (products.length === 0) return;
    const interval = setInterval(handleNext, AUTO_SLIDE_INTERVAL);
    return () => clearInterval(interval);
  }, [currentIndex, maxIndex, products.length]);

  const visibleProducts = products.slice(
    currentIndex * productsPerPage,
    (currentIndex * productsPerPage) + productsPerPage
  );

  const styles = {
    section: {
      padding: '60px 0',
      backgroundColor: '#fff',
      fontFamily: "'Rubik', sans-serif",
      overflow: 'hidden',
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px',
      position: 'relative',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '40px',
    },
    title: {
      fontSize: '28px',
      fontWeight: '800',
      color: DARK,
      margin: 0,
    },
    controls: {
      display: 'flex',
      gap: '12px',
    },
    arrowBtn: {
      width: '44px',
      height: '44px',
      borderRadius: '50%',
      border: '1px solid #eee',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#fff',
      color: DARK,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
    },
    sliderTrack: {
      display: 'grid',
      gridTemplateColumns: `repeat(${productsPerPage}, 1fr)`,
      gap: '24px',
      minHeight: '380px',
    },
    card: {
      backgroundColor: '#fff',
      borderRadius: '20px',
      overflow: 'hidden',
      border: '1px solid #f0f0f0',
      transition: 'all 0.3s ease',
      height: '100%',
      cursor: 'pointer',
    },
    imageBox: {
      width: '100%',
      height: '200px',
      overflow: 'hidden',
      backgroundColor: '#f8f9fa',
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    content: {
      padding: '20px',
    },
    badge: {
      backgroundColor: `${ORANGE}10`,
      color: ORANGE,
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '10px',
      fontWeight: '700',
      textTransform: 'uppercase',
      marginBottom: '10px',
      display: 'inline-block',
    },
    prodName: {
      fontSize: '15px',
      fontWeight: '600',
      color: DARK,
      marginBottom: '10px',
      height: '42px',
      lineHeight: 1.4,
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      textOverflow: 'ellipsis',
    },
    priceBox: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    price: {
      fontSize: '18px',
      fontWeight: '700',
      color: ORANGE,
      margin: 0,
    },
    oldPrice: {
      fontSize: '14px',
      color: '#aaa',
      textDecoration: 'line-through',
      margin: 0,
    },
  };

  return (
    <section style={styles.section}>
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.title}>Recently Added</h3>
          <div style={styles.controls}>
            <button 
              style={styles.arrowBtn} 
              onClick={handlePrev}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = ORANGE; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = DARK; }}
            >
              <IoIosArrowBack size={20} />
            </button>
            <button 
              style={styles.arrowBtn} 
              onClick={handleNext}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = ORANGE; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = DARK; }}
            >
              <IoIosArrowForward size={20} />
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : products.length > 0 ? (
          <div style={{ position: 'relative' }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
                style={styles.sliderTrack}
              >
                {visibleProducts.map((product, index) => {
                  const imageUrl = product.images?.[0]?.url
                    ? `${base_url}${product.images[0].url}`
                    : 'https://via.placeholder.com/200x200?text=No+Image';

                  const selectedVariant = product.variants?.[0] || null;
                  const currentPrice = selectedVariant ? selectedVariant.CurrentPrice : product.CurrentPrice;
                  const offerPrice = selectedVariant ? selectedVariant.offerPrice : product.offerPrice;
                  const displayPrice = offerPrice && offerPrice > 0 ? offerPrice : currentPrice;
                  const showOldPrice = offerPrice > 0 && currentPrice > 0 && offerPrice < currentPrice;

                  return (
                    <motion.div 
                      key={product._id || index}
                      style={styles.card}
                      whileHover={{ y: -10, boxShadow: '0 15px 35px rgba(0,0,0,0.1)' }}
                      onClick={() => navigate(`/product/${product?._id}`)}
                    >
                      <div style={styles.imageBox}>
                        <img src={imageUrl} alt={product.title} style={styles.image} />
                      </div>
                      <div style={styles.content}>
                        <span style={styles.badge}>New Arrival</span>
                        <h6 style={styles.prodName}>{product.title}</h6>
                        <div style={styles.priceBox}>
                          <p style={styles.price}>₹{displayPrice}</p>
                          {showOldPrice && (
                            <p style={styles.oldPrice}>₹{currentPrice}</p>
                          )}
                          {selectedVariant && (
                            <span style={{ fontSize: '13px', color: '#666', marginLeft: 'auto' }}>
                              / {selectedVariant.measurementUnit}
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </AnimatePresence>
          </div>
        ) : (
          <p style={{ textAlign: 'center', color: '#888', padding: '40px' }}>No new products found.</p>
        )}
      </div>
    </section>
  );
};

export default RecentlyAddProducts;